import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '../AppContext';
import './Products.css';

const SORT_OPTIONS = [
  { value: 'default',    label: 'Default' },
  { value: 'name-asc',  label: 'Name: A → Z' },
  { value: 'name-desc', label: 'Name: Z → A' },
  { value: 'price-asc', label: 'Price: Low → High' },
  { value: 'price-desc',label: 'Price: High → Low' },
];

// Parse numeric price from a string like "10,500.00" → 10500
const parsePrice = (str) => {
  if (!str) return 0;
  const n = parseFloat(String(str).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};

// Wishlist stored in localStorage (simple toggle)
const getWishlist = () => {
  try { return new Set(JSON.parse(localStorage.getItem('zinga_wishlist') || '[]')); }
  catch { return new Set(); }
};
const saveWishlist = (set) => {
  localStorage.setItem('zinga_wishlist', JSON.stringify([...set]));
};

const StarRating = () => (
  <div className="prd-stars" aria-label="5 star rating">
    {[1,2,3,4,5].map(i => (
      <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
    <span className="prd-rating-count">(12)</span>
  </div>
);

const WishBtn = ({ id, wishlist, toggleWish }) => {
  const active = wishlist.has(id);
  return (
    <button
      className={'prd-wish-btn' + (active ? ' prd-wish-active' : '')}
      onClick={e => { e.preventDefault(); toggleWish(id); }}
      title={active ? 'Remove from wishlist' : 'Add to wishlist'}
      aria-label="Wishlist"
    >
      <svg width="16" height="16" viewBox="0 0 24 24"
        fill={active ? '#ef4444' : 'none'}
        stroke={active ? '#ef4444' : '#94a3b8'}
        strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>
  );
};

const Products = () => {
  const { products } = useAppData();
  const [search,       setSearch]       = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy,       setSortBy]       = useState('default');
  const [viewMode,     setViewMode]     = useState('grid'); // 'grid' | 'list'
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [priceRange,   setPriceRange]   = useState([0, 20000]);
  const [wishlist,     setWishlist]     = useState(getWishlist);

  // Derive unique categories from product data
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];
    return cats;
  }, [products]);

  // Price bounds from actual data
  const [minPrice, maxPrice] = useMemo(() => {
    const prices = products.map(p => parsePrice(p.price)).filter(n => n > 0);
    return prices.length ? [0, Math.max(...prices)] : [0, 20000];
  }, [products]);

  const toggleWish = (id) => {
    setWishlist(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      saveWishlist(next);
      return next;
    });
  };

  const resetFilters = () => {
    setSearch('');
    setActiveCategory('All');
    setSortBy('default');
    setPriceRange([0, maxPrice]);
  };

  // Apply all filters + sort
  const filtered = useMemo(() => {
    let list = [...products];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q));
    }
    if (activeCategory !== 'All') {
      list = list.filter(p => p.category === activeCategory);
    }
    list = list.filter(p => {
      const n = parsePrice(p.price);
      return n === 0 || (n >= priceRange[0] && n <= priceRange[1]);
    });
    switch (sortBy) {
      case 'name-asc':   list.sort((a,b) => a.name.localeCompare(b.name)); break;
      case 'name-desc':  list.sort((a,b) => b.name.localeCompare(a.name)); break;
      case 'price-asc':  list.sort((a,b) => parsePrice(a.price) - parsePrice(b.price)); break;
      case 'price-desc': list.sort((a,b) => parsePrice(b.price) - parsePrice(a.price)); break;
      default: break;
    }
    return list;
  }, [products, search, activeCategory, sortBy, priceRange]);

  const activeFiltersCount = (search ? 1 : 0) + (activeCategory !== 'All' ? 1 : 0) + (sortBy !== 'default' ? 1 : 0);

  return (
    <div className="prd-page">

      {/* ── Hero Banner ───────────────────────────────────────────────── */}
      <div className="prd-hero">
        <div className="prd-hero-inner">
          <span className="prd-hero-eyebrow">All Products</span>
          <h1 className="prd-hero-title">ZINGA® Protective Coatings</h1>
          <p className="prd-hero-sub">96% Zinc · Cathodic Protection · Made in Belgium</p>
        </div>
        <div className="prd-hero-stats">
          <div className="prd-hero-stat"><span>{products.length}</span>Products</div>
          <div className="prd-hero-divider" />
          <div className="prd-hero-stat"><span>{categories.length - 1}</span>Categories</div>
          <div className="prd-hero-divider" />
          <div className="prd-hero-stat"><span>45+</span>Years Experience</div>
        </div>
      </div>

      {/* ── Main Body ─────────────────────────────────────────────────── */}
      <div className="prd-body">

        {/* Mobile filter toggle */}
        <button className="prd-mobile-filter-btn" onClick={() => setSidebarOpen(v => !v)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
          Filters {activeFiltersCount > 0 && <span className="prd-filter-badge">{activeFiltersCount}</span>}
        </button>

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && <div className="prd-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* ── Sidebar ───────────────────────────────────────────────── */}
        <aside className={'prd-sidebar' + (sidebarOpen ? ' prd-sidebar--open' : '')}>
          <div className="prd-sidebar-header">
            <span className="prd-sidebar-title">Filters</span>
            {activeFiltersCount > 0 && (
              <button className="prd-clear-all" onClick={resetFilters}>Clear all</button>
            )}
            <button className="prd-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
          </div>

          {/* Search */}
          <div className="prd-filter-section">
            <div className="prd-filter-label">Search</div>
            <div className="prd-search-wrap">
              <svg className="prd-search-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                className="prd-search-input"
                type="text"
                placeholder="Search products…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="prd-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="prd-filter-section">
            <div className="prd-filter-label">Category</div>
            {categories.map(cat => (
              <button
                key={cat}
                className={'prd-cat-btn' + (activeCategory === cat ? ' prd-cat-active' : '')}
                onClick={() => setActiveCategory(cat)}
              >
                <span>{cat}</span>
                <span className="prd-cat-count">
                  {cat === 'All' ? products.length : products.filter(p => p.category === cat).length}
                </span>
              </button>
            ))}
          </div>

          {/* Price Range */}
          <div className="prd-filter-section">
            <div className="prd-filter-label">
              Price Range
              <span className="prd-range-vals">₹{priceRange[0].toLocaleString('en-IN')} – ₹{priceRange[1].toLocaleString('en-IN')}</span>
            </div>
            <input
              type="range"
              className="prd-range-slider"
              min={minPrice}
              max={maxPrice}
              step={100}
              value={priceRange[1]}
              onChange={e => setPriceRange([priceRange[0], Number(e.target.value)])}
            />
            <div className="prd-range-labels">
              <span>₹0</span><span>₹{maxPrice.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Sort (in sidebar for mobile) */}
          <div className="prd-filter-section prd-filter-section--sort">
            <div className="prd-filter-label">Sort By</div>
            <select className="prd-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </aside>

        {/* ── Right: Toolbar + Grid ──────────────────────────────────── */}
        <div className="prd-main">

          {/* Toolbar */}
          <div className="prd-toolbar">
            <div className="prd-toolbar-left">
              <span className="prd-result-count">
                <strong>{filtered.length}</strong> product{filtered.length !== 1 ? 's' : ''} found
              </span>
              {/* Active filter chips */}
              {activeCategory !== 'All' && (
                <span className="prd-chip">
                  {activeCategory}
                  <button onClick={() => setActiveCategory('All')}>✕</button>
                </span>
              )}
              {search && (
                <span className="prd-chip">
                  "{search}"
                  <button onClick={() => setSearch('')}>✕</button>
                </span>
              )}
            </div>
            <div className="prd-toolbar-right">
              {/* Sort dropdown (desktop only) */}
              <div className="prd-sort-wrap prd-desktop-only">
                <label className="prd-sort-label">Sort:</label>
                <select className="prd-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {/* View toggle */}
              <div className="prd-view-toggle">
                <button
                  className={'prd-view-btn' + (viewMode === 'grid' ? ' prd-view-active' : '')}
                  onClick={() => setViewMode('grid')} title="Grid view"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                </button>
                <button
                  className={'prd-view-btn' + (viewMode === 'list' ? ' prd-view-active' : '')}
                  onClick={() => setViewMode('list')} title="List view"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                </button>
              </div>
            </div>
          </div>

          {/* Grid / List */}
          {filtered.length > 0 ? (
            <div className={'prd-grid' + (viewMode === 'list' ? ' prd-list' : '')}>
              {filtered.map((product, idx) => {
                const img = product.images?.[0] || product.image;
                const priceNum = parsePrice(product.price);
                const isNew = idx < 3;
                return (
                  <div key={product.id} className="prd-card">
                    <WishBtn id={product.id} wishlist={wishlist} toggleWish={toggleWish} />
                    {isNew && <span className="prd-badge prd-badge--new">New</span>}
                    {product.category && <span className="prd-badge prd-badge--cat">{product.category}</span>}

                    <Link to={'/product/' + product.id} className="prd-card-img-link">
                      <div className="prd-card-img-wrap">
                        {img
                          ? <img src={img} alt={product.name} className="prd-card-img" loading="lazy" onError={e => { e.target.style.display='none'; }} />
                          : <div className="prd-card-img-placeholder"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>
                        }
                      </div>
                    </Link>

                    <div className="prd-card-body">
                      {product.category && <span className="prd-card-cat">{product.category}</span>}
                      <Link to={'/product/' + product.id} className="prd-card-name-link">
                        <h3 className="prd-card-name">{product.name}</h3>
                      </Link>
                      <StarRating />
                      <div className="prd-card-footer">
                        <div className="prd-card-price">
                          {priceNum > 0 ? <>₹<span>{Number(priceNum).toLocaleString('en-IN')}</span></> : <span className="prd-price-contact">Contact Us</span>}
                        </div>
                        <Link to={'/product/' + product.id} className="prd-card-cta">
                          Details
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:4}}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                        </Link>
                      </div>
                      <div className="prd-card-stock">
                        <span className="prd-in-stock-dot" />
                        In Stock
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="prd-empty">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <h3>No products found</h3>
              <p>Try adjusting your filters or search query.</p>
              <button className="prd-reset-btn" onClick={resetFilters}>Clear Filters</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
